/**
 * Date helpers that all work in *local* time.
 *
 * Deliberately avoiding `toISOString()` for calendar dates: it converts to UTC
 * first, so a 9pm workout in Riga would get filed under tomorrow for half the
 * year. Everything here formats from the local components instead.
 */

export function todayIso(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function daysBetween(fromIso: string, toIso: string): number {
  const a = parseIso(fromIso).getTime();
  const b = parseIso(toIso).getTime();
  return Math.round((b - a) / 86_400_000);
}

export function addDays(iso: string, n: number): string {
  const d = parseIso(iso);
  d.setDate(d.getDate() + n);
  return todayIso(d);
}

export function hoursSince(isoDateTime: string, now: Date = new Date()): number {
  return (now.getTime() - new Date(isoDateTime).getTime()) / 3_600_000;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function dayName(dayNumber: number): string {
  return DAY_NAMES[dayNumber] ?? '';
}

export function dayShort(dayNumber: number): string {
  return DAY_SHORT[dayNumber] ?? '';
}

export function formatDate(iso: string): string {
  const d = parseIso(iso);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

export function formatRelative(iso: string, today: string = todayIso()): string {
  const diff = daysBetween(iso, today);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff === -1) return 'Tomorrow';
  if (diff > 1 && diff < 7) return `${diff} days ago`;
  return formatDate(iso);
}

/** Monday-anchored week key, e.g. "2026-W38", for weekly rollups. */
export function weekKey(iso: string): string {
  const d = parseIso(iso);
  const day = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - day + 3); // Thursday of this week
  const thursday = d.getTime();
  const jan1 = new Date(d.getFullYear(), 0, 1);
  const week = 1 + Math.round((thursday - jan1.getTime()) / 86_400_000 / 7);
  return `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

/** The Monday of the week containing `iso`. */
export function startOfWeek(iso: string): string {
  const d = parseIso(iso);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return todayIso(d);
}

export function minutesBetween(startIso: string, endIso: string): number {
  return Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60_000);
}
